/**
 * One headline number on a dashboard.
 *
 * Lives in the design system rather than the admin app because it is a pure
 * composition of the named type scale, the radius and shadow tokens and the semantic
 * palette, with no data model of its own — structurally the same kind of thing as
 * `DetailHeader` and the state blocks. The charts that sit beside it do know about
 * series and breakdown rows, so they stay in the app: putting a data model in here
 * is how a design system starts rotting.
 *
 * Everything arrives already localised and already formatted. This package ships no
 * catalogue, and a decimal separator is a locale decision the consumer has made.
 */

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import { RADIUS, SHADOW } from "../theme/tokens";

export interface StatTileDelta {
  /** Signed percentage change against the comparison window. */
  percent: number;
  /**
   * Whether up is good. False inverts the colour — a rising drop-off is not a win.
   */
  higherIsBetter?: boolean;
  /** Screen-reader description, e.g. "up 12 percent against the previous 30 days". */
  label: string;
}

export interface StatTileProps {
  label: string;
  /** Already formatted; the tile never sees a raw number. */
  value: string;
  caption?: string;
  delta?: StatTileDelta;
  /** Optional sparkline or similar, rendered under the value. */
  chart?: ReactNode;
  testId?: string;
}

export function StatTile({
  label,
  value,
  caption,
  delta,
  chart,
  testId,
}: StatTileProps) {
  const positive = delta ? delta.percent >= 0 : false;
  const good = delta ? positive === (delta.higherIsBetter ?? true) : false;

  return (
    <Paper
      variant="outlined"
      data-testid={testId}
      sx={{
        // A string, because inside `sx` a bare number is multiplied by
        // `theme.shape.borderRadius` rather than read as pixels.
        borderRadius: `${RADIUS.lg}px`,
        boxShadow: SHADOW.card,
        padding: { xs: 2, md: 2.5 },
        height: "100%",
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="OV1" sx={{ color: "text.muted" }}>
          {label}
        </Typography>
        <Typography
          variant="H2"
          component="p"
          // A font feature, not a size override, so the named-variant rule holds.
          // Tabular figures stop the number jittering when the range changes.
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {value}
        </Typography>
        {chart}
        {(caption || delta) && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "baseline", flexWrap: "wrap" }}
          >
            {delta && (
              <Typography
                variant="L2"
                aria-label={delta.label}
                sx={{
                  color: good ? "success.main" : "error.main",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {positive ? "▲" : "▼"} {Math.abs(delta.percent).toFixed(0)}%
              </Typography>
            )}
            {caption && (
              <Typography variant="T3" sx={{ color: "text.muted" }}>
                {caption}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
